import React, { type PropsWithChildren, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Login from '../Login';
import {
  beginOAuthLogin,
  finishOAuthLogin,
  getAccessToken,
  isOAuthCallback,
  OAUTH_SESSION_CLEARED_EVENT,
  restoreOAuthSessionFromPeer,
  startOAuthSessionSync,
} from '../../auth/oauth';
import { OAUTH_EXPIRED_EVENT } from '../../KKuTuIOApi';
import { fetchUser, logout } from '../../features/session/sessionSlice';
import type { AppDispatch, RootState } from './store';

function messageFrom(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function UserAuthentication({ children }: PropsWithChildren) {
  const dispatch = useDispatch<AppDispatch>();
  const loggedIn = useSelector((state: RootState) => state.user.loggedIn);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      startOAuthSessionSync();
      if (isOAuthCallback()) {
        try {
          const returnPath = await finishOAuthLogin();
          window.location.replace(returnPath);
          return;
        } catch (loginError) {
          if (active) setError(messageFrom(loginError, 'OAuth 로그인에 실패했습니다.'));
        }
      } else {
        await restoreOAuthSessionFromPeer();
      }
      if (!active) return;
      const hadAccessToken = Boolean(getAccessToken());
      const profileResult = await dispatch(fetchUser());
      if (active && fetchUser.fulfilled.match(profileResult) && profileResult.payload === null && hadAccessToken) {
        setError('해당 기능을 이용할 수 있는 권한이 없습니다.');
      }
      if (active) setReady(true);
    };
    const expired = () => {
      dispatch(logout());
      setReady(true);
    };
    window.addEventListener(OAUTH_EXPIRED_EVENT, expired);
    window.addEventListener(OAUTH_SESSION_CLEARED_EVENT, expired);
    void load();
    return () => {
      active = false;
      window.removeEventListener(OAUTH_EXPIRED_EVENT, expired);
      window.removeEventListener(OAUTH_SESSION_CLEARED_EVENT, expired);
    };
  }, [dispatch]);

  const login = async () => {
    try {
      setError('');
      await beginOAuthLogin(`${window.location.pathname}${window.location.search}`);
    } catch (loginError) {
      setError(messageFrom(loginError, 'OAuth 로그인을 시작하지 못했습니다.'));
    }
  };

  if (!ready && !error) return <Login loading onLogin={login}/>;
  return loggedIn ? children : <Login error={error} onLogin={login}/>;
}
